import { useState, useEffect, useContext } from 'react';
import { Form, Row, Col, Button, Card } from 'react-bootstrap';
import Select from 'react-select';

import SingleDatePicker from '../../../components/common/SingleDatePicker/SingleDatePicker';
import TimeSlotSlider from './TimeSlotSlider';
import AutoCompleteInput from '../../../components/common/AutoComplete/AutoCompleteInput';

import { useGetRestaurantHolidays, useGetRestaurantOutlets } from '../../../APIs/common';

import { useAddBooking, useGetServicePeriods, useGetAvailableTimeSlots } from '../../../APIs/BookingManagement/booking';

import { ConfigContext } from '../../../contexts/ConfigContext';
import { reactSelectDark } from '../../../styles/reactSelectDarkTheme';
import { toYMD } from '../../../utils/FormatDate';
import { notify } from '@common/NotificationService';
import DisabledFieldWrapper from '../../../components/common/DisabledFieldWrapper/DisabledFieldWrapper';
import LoyaltyOptIn from './LoyaltyOptIn';
import './AddBookingForm.css';

function useClickOutside(ref, callback) {
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) callback();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [ref, callback]);
}

export default function AddBookingForm({ initialValues = {}, onSuccess, onCancel, isModal = false }) {
  const config = useContext(ConfigContext);

  const [city, setCity] = useState(null);
  const [selectedCityId, setSelectedCityId] = useState(null);

  const [location, setLocation] = useState(null);
  const [locationOptions, setLocationOptions] = useState([]);

  const [date, setDate] = useState(null);
  const [holidays, setHolidays] = useState([]);
  const [nonWorkingDays, setNonWorkingDays] = useState([]);

  const [guestCount, setGuestCount] = useState('');

  const [servicePeriod, setServicePeriod] = useState(null);
  const [servicePeriodOptions, setServicePeriodOptions] = useState([]);

  const [amenities, setAmenities] = useState([]);
  const [amenitiesOptions, setAmenitiesOptions] = useState([]);

  const [timeSlots, setTimeSlots] = useState([]);
  const [timeSlot, setTimeSlot] = useState(null);
  const [selectedSlotMeta, setSelectedSlotMeta] = useState(null);

  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [adminComment, setAdminComment] = useState('');
  const [modeOfBooking, setModeOfBooking] = useState('manual');
  // Ticked by default: staff should not have to remember. It records an
  // intention only — the counter confirms it out loud on the first visit.
  const [joinLoyalty, setJoinLoyalty] = useState(true);

  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const { outlets } = useGetRestaurantOutlets({
    location_id: selectedCityId
  });

  const { getRestaurantHolidays } = useGetRestaurantHolidays();
  const { getServicePeriods } = useGetServicePeriods();
  const { getAvailableTimeSlots } = useGetAvailableTimeSlots();
  const { addBooking, isMutating: isBookingCreating } = useAddBooking();

  useEffect(() => {
    if (initialValues?.date) setDate(initialValues.date);
    if (initialValues?.locationId) {
      setLocation({ value: initialValues.locationId, label: 'Selected Location' });
    }
  }, [initialValues]);

  useEffect(() => {
    if (!outlets?.length) return;

    setLocationOptions(
      outlets.map((o) => ({
        value: o.id,
        label: o.name
      }))
    );
  }, [outlets]);

  useEffect(() => {
    if (!selectedSlotMeta || !guestCount) return;

    if (guestCount > selectedSlotMeta.availableSeats) {
      setErrors((e) => ({
        ...e,
        guestCount: `Only ${selectedSlotMeta.availableSeats} seats available`
      }));
    } else {
      setErrors((e) => ({ ...e, guestCount: undefined }));
    }
  }, [selectedSlotMeta, guestCount]);

  const formatAuPhone = (phone) => {
    if (!phone) return '';
    const cleaned = phone.replace(/\D/g, '');
    return cleaned.startsWith('61') ? cleaned : `61${cleaned}`;
  };

  const validateForm = () => {
    const e = {};

    if (!city) e.city = 'City is required';
    if (!location) e.location = 'Location is required';
    if (!date) e.date = 'Date is required';
    if (!guestCount || guestCount <= 0) e.guestCount = 'Enter valid guests';
    if (!servicePeriod) e.servicePeriod = 'Service period required';
    if (!timeSlot) e.timeSlot = 'Select a time slot';
    if (!customerName.trim()) e.customerName = 'Customer name required';

    if (!customerPhone.trim()) {
      e.customerPhone = 'Phone number is required';
    } else if (!/^(\+?61[2-9]\d{8}|0[2-9]\d{8})$/.test(customerPhone.trim().replace(/[\s\-]/g, ''))) {
      e.customerPhone = 'Enter a valid Australian phone number (e.g. 0412 345 678)';
    }

    if (customerEmail && !customerEmail.match(/^\S+@\S+\.\S+$/)) {
      e.customerEmail = 'Invalid email';
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleCitySelect = (item) => {
    setCity(item);
    setSelectedCityId(item.value);
    setLocation(null);
    setDate(null);
    setGuestCount('');
    setServicePeriod(null);
    setAmenities([]);
    setTimeSlots([]);
  };

  const handleLocationSelect = async (sel) => {
    setLocation(sel);
    setDate(null);
    setGuestCount('');
    setServicePeriod(null);
    setAmenities([]);
    setTimeSlots([]);

    const res = await getRestaurantHolidays(sel.value);
    setHolidays(res?.holidays || []);
    setNonWorkingDays(res?.nonWorkingDays || []);
  };

  const handleDateSelect = (selectedDate) => {
    setDate(selectedDate);

    // Reset
    setGuestCount('');
    setServicePeriod(null);
    setAmenities([]);
    setTimeSlots([]);
    setTimeSlot(null);
  };

  const handleGuestChange = async (e) => {
    const value = Number(e.target.value);
    setGuestCount(value);
    setServicePeriod(null);
    setAmenities([]);
    setTimeSlots([]);
    setSelectedSlotMeta(null);

    if (location?.value && date && value > 0) {
      const res = await getServicePeriods({
        location_id: location.value,
        date,
        no_of_guests: value
      });

      setServicePeriodOptions(
        res.servicePeriods.map((sp) => ({
          value: sp.id,
          label: sp.name
        }))
      );

      setAmenitiesOptions(
        res.preferences.map((p) => ({
          value: p.id,
          label: p.name
        }))
      );
    }
  };

  const handleServiceOrAmenitiesSelected = async (sp = servicePeriod) => {
    if (!location || !date || !guestCount || !sp) return;

    const res = await getAvailableTimeSlots({
      location_id: location.value,
      date,
      service_period_id: sp.value,
      no_of_guests: guestCount
    });

    setTimeSlots(
      (res.timeSlots || []).map((s) => ({
        label: s.time,
        value: s.time_24h,
        availableSeats: s.available_seats
      }))
    );
  };

  // const handleSubmit = async (e) => {
  //   e.preventDefault();
  //   if (!validateForm()) return;
  //   setIsSaving(true);
  //   const payload = {
  //     location_id: location.value,
  //     service_period_id: servicePeriod.value,
  //     date: toYMD(date),
  //     time: timeSlot,
  //     no_of_guests: guestCount,
  //     customer_name: customerName,
  //     customer_phone: formatAuPhone(customerPhone),
  //     customer_email: customerEmail,
  //     amenities: amenities.map((a) => a.value),
  //     internal_notes: adminComment,
  //     mode_of_booking: modeOfBooking,
  //     auto_confirm: true
  //   };

  //   const res = await addBooking(payload);

  //   if (res?.success) {
  //     notify.success(res.message || 'Booking created');
  //     onSuccess?.();
  //   } else {
  //     notify.error(res?.message || 'Failed to create booking');
  //   }
  // };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSaving(true);

    try {
      const payload = {
        location_id: location.value,
        service_period_id: servicePeriod.value,
        date: toYMD(date),
        time: timeSlot,
        no_of_guests: guestCount,
        customer_name: customerName,
        customer_phone: formatAuPhone(customerPhone),
        customer_email: customerEmail,
        amenities: amenities.map((a) => a.value),
        internal_notes: adminComment,
        mode_of_booking: modeOfBooking,
        join_loyalty: joinLoyalty,
        auto_confirm: true
      };

      const res = await addBooking(payload);

      if (res?.success) {
        notify.success(res.message || 'Booking created');
        onSuccess?.();
      } else {
        notify.error(res?.message || 'Failed to create booking');
      }
    } catch (error) {
      notify.error(error?.message || 'Something went wrong while creating booking');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card className={`p-4 add-booking-card ${isModal ? 'border-0' : ''}`}>
      <Form onSubmit={handleSubmit}>
        <Row className="mt-3">
          {/* CITY */}
          <Col lg={3} md={6} sm={12} className="mb-3 mb-md-0">
            <Form.Label>
              City <span className="color-danger">*</span>
            </Form.Label>
            <AutoCompleteInput
              value={city?.label || ''}
              apiEndpoint="/restaurant-location/get-location"
              placeholder="Search City"
              onChange={(text) => setCity({ label: text, value: text })}
              onSelect={(item) => {
                handleCitySelect({ label: item.name, value: item.id });
                setLocationOptions([]);
              }}
            />
            {errors.city && <div className="text-danger small">{errors.city}</div>}
          </Col>

          <Col lg={3} md={6} sm={12} className="mb-3 mb-md-0">
            <Form.Label>
              Location <span className="color-danger">*</span>
            </Form.Label>

            <DisabledFieldWrapper disabled={!selectedCityId} reason="Please select a city to enable location">
              <div>
                <Select
                  options={locationOptions}
                  value={location}
                  onChange={handleLocationSelect}
                  placeholder="Select Location"
                  isDisabled={!selectedCityId}
                  styles={config.mode === 'dark' ? reactSelectDark : {}}
                />
              </div>
            </DisabledFieldWrapper>

            {errors.location && <div className="text-danger small">{errors.location}</div>}
          </Col>

          <Col lg={3} md={6} sm={12} className="mb-3 mb-md-0">
            <Form.Label>
              Date <span className="color-danger">*</span>
            </Form.Label>

            <DisabledFieldWrapper disabled={!location} reason="Please select a location first">
              <div>
                <SingleDatePicker
                  disabled={!location}
                  value={date}
                  holidays={holidays.map((h) => h.date)}
                  onSelect={handleDateSelect}
                  useClickOutside={useClickOutside}
                  nonWorkingDays={nonWorkingDays}
                />
              </div>
            </DisabledFieldWrapper>

            {errors.date && <div className="text-danger small">{errors.date}</div>}
          </Col>

          <Col lg={3} md={6} sm={12} className="mb-3 mb-md-0">
            <Form.Label>
              No. of Guests <span className="color-danger">*</span>
            </Form.Label>

            <DisabledFieldWrapper disabled={!location || !date} reason="Select location and date to enter guest count">
              <Form.Control
                type="number"
                value={guestCount}
                onChange={handleGuestChange}
                disabled={!location || !date}
                placeholder="Enter guests"
                className="theme-input"
              />
            </DisabledFieldWrapper>

            {errors.guestCount && <div className="text-danger small">{errors.guestCount}</div>}
          </Col>

          <Col lg={4} md={6} sm={12} className="mt-3 mb-3 mb-md-0">
            <Form.Label>
              Service Period <span className="color-danger">*</span>
            </Form.Label>

            <DisabledFieldWrapper disabled={!location} reason="Please select a location first">
              <div>
                <Select
                  options={servicePeriodOptions}
                  value={servicePeriod}
                  onChange={(sel) => {
                    setServicePeriod(sel);
                    handleServiceOrAmenitiesSelected(sel, amenities);
                  }}
                  isDisabled={!servicePeriodOptions.length}
                  placeholder="Select Service Period"
                  styles={config.mode === 'dark' ? reactSelectDark : {}}
                />
              </div>
            </DisabledFieldWrapper>

            {errors.servicePeriod && <div className="text-danger small">{errors.servicePeriod}</div>}
          </Col>

          <Col lg={4} md={6} sm={12} className="mt-3 mb-3 mb-md-0">
            <Form.Label>Preference Amenities</Form.Label>
            <DisabledFieldWrapper disabled={!location} reason="Please select a location first">
              <div>
                <Select
                  isMulti
                  options={amenitiesOptions}
                  value={amenities}
                  onChange={(sel) => {
                    setAmenities(sel);
                    handleServiceOrAmenitiesSelected(servicePeriod, sel);
                  }}
                  isDisabled={!amenitiesOptions.length}
                  placeholder="Select Amenities"
                  styles={config.mode === 'dark' ? reactSelectDark : {}}
                />
              </div>
            </DisabledFieldWrapper>
            {/* {errors.amenities && <div className="text-danger small">{errors.amenities}</div>} */}
          </Col>

          <Col lg={4} md={12} sm={12} className="mt-3">
            <Form.Label>
              Mode of Booking <span className="color-danger">*</span>
            </Form.Label>
            <div className="d-flex gap-4 align-items-center flex-wrap">
              <Form.Check
                type="radio"
                label="Manual"
                value="manual"
                checked={modeOfBooking === 'manual'}
                onChange={(e) => setModeOfBooking(e.target.value)}
              />
              <Form.Check
                type="radio"
                label="Phone Call"
                value="phone"
                checked={modeOfBooking === 'phone'}
                onChange={(e) => setModeOfBooking(e.target.value)}
              />
            </div>
            {errors.modeOfBooking && <div className="text-danger small">{errors.modeOfBooking}</div>}
          </Col>

          {timeSlots.length > 0 && (
            <Col xs={12} className="mt-4">
              <Form.Label>
                Time Slots <span className="color-danger">*</span>
              </Form.Label>

              <TimeSlotSlider
                slots={timeSlots}
                selected={timeSlot}
                onSelect={(slotValue) => {
                  const slotObj = timeSlots.find((s) => s.value === slotValue);

                  setTimeSlot(slotValue);
                  setSelectedSlotMeta(slotObj);

                  // Reset guest error if valid
                  if (guestCount && slotObj && guestCount <= slotObj.availableSeats) {
                    setErrors((e) => ({ ...e, guestCount: undefined }));
                  }
                }}
              />

              {errors.timeSlot && <div className="text-danger small">{errors.timeSlot}</div>}
            </Col>
          )}

          {timeSlots.length > 0 && (
            <>
              <Col lg={4} md={6} sm={12} className="mt-3 mb-3 mb-md-0">
                <Form.Label>
                  Customer Name <span className="color-danger">*</span>
                </Form.Label>
                <Form.Control value={customerName} onChange={(e) => setCustomerName(e.target.value)} />
                {errors.customerName && <div className="text-danger small">{errors.customerName}</div>}
              </Col>

              <Col lg={4} md={6} sm={12} className="mt-3 mb-3 mb-md-0">
                <Form.Label>
                  Customer Phone <span className="color-danger">*</span>
                </Form.Label>
                <div className="d-flex">
                  <span className="input-group-text">+61</span>
                  <Form.Control
                    value={customerPhone}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 9);
                      setCustomerPhone(value);
                    }}
                    placeholder="431231231"
                  />
                </div>

                {errors.customerPhone && <div className="text-danger small">{errors.customerPhone}</div>}
              </Col>

              <Col lg={4} md={12} sm={12} className="mt-3">
                <Form.Label>Customer Email</Form.Label>
                <Form.Control value={customerEmail} onChange={(e) => setCustomerEmail(e.target.value)} />
                {errors.customerEmail && <div className="text-danger small">{errors.customerEmail}</div>}
              </Col>

              <Col xs={12} className="mt-3">
                <Form.Label>Admin Comment</Form.Label>
                <Form.Control as="textarea" rows={3} value={adminComment} onChange={(e) => setAdminComment(e.target.value)} />
              </Col>

              <Col xs={12} className="mt-3">
                <LoyaltyOptIn phone={customerPhone ? `0${customerPhone}` : ''} checked={joinLoyalty} onChange={setJoinLoyalty} />
              </Col>
            </>
          )}
        </Row>

        {/* {timeSlots.length > 0 && (
          <Button className="mt-4" type="submit" disabled={isBookingCreating}>
            Create Booking
          </Button>
        )}

        {isModal && (
          <Button variant="secondary" className="mt-4 ms-3" onClick={onCancel}>
            Cancel
          </Button>
        )} */}
        <div className="d-flex justify-content-end mt-4 gap-3">
          {timeSlots.length > 0 && (
            <Button type="submit" disabled={isSaving || isBookingCreating}>
              {isSaving ? 'Creating...' : 'Create Booking'}
            </Button>
          )}

          {isModal && (
            <Button variant="secondary" className="ms-3" onClick={onCancel}>
              Cancel
            </Button>
          )}
        </div>
      </Form>
    </Card>
  );
}
